import { useEffect, useRef, useState } from "react";
import { track } from "@/lib/analytics";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Camera, UploadSimple, PencilSimple, Sparkle, LinkSimple, InstagramLogo, TiktokLogo, ShareNetwork, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { analysePhoto, autoFixImage } from "@/lib/photoQuality";
import { useAuth } from "@/contexts/AuthContext";
import PageMeta from "@/components/PageMeta";
import PaywallModal from "@/components/PaywallModal";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";

export default function ScanRecipe() {
  const [params] = useSearchParams();
  const presetCb = params.get("cookbook_id");
  // `return_to` lets the caller (e.g. a cookbook page hitting "Add recipe")
  // ask us to send the user BACK to that cookbook after the recipe saves,
  // instead of the default recipe view. Forwarded through /scan → sessionStorage
  // → OCRSandbox so the whole pipeline honours it.
  const presetReturnTo = params.get("return_to");
  const initialMode = (() => {
    const m = (params.get("mode") || "").toLowerCase();
    return ["url", "manual", "scan", "paste"].includes(m) ? m : "scan";
  })();
  const prefilledUrl = (params.get("url") || "").trim();
  const nav = useNavigate();
  const { user } = useAuth();
  const { flags, limits } = useSiteFlags();
  // Free-tier paywall state: when the backend returns HTTP 402 we surface a
  // polite modal instead of a hard-to-notice toast, so free users clearly know
  // they need to pick a plan (or wait for launch) to keep saving recipes.
  const [paywall, setPaywall] = useState({ open: false, message: "" });
  // Multi-page scan: pages[] is the list of selected files. Single-page
  // remains the default — if the user only picks one, behaviour is identical
  // to before. Adding a second page enables the "merge into one recipe" flow.
  const [pages, setPages] = useState([]);  // [{ file, previewUrl }]
  const [loading, setLoading] = useState(false);
  // Auto-improve toggle (defaults ON). When ON, `startScan` runs a silent
  // brightness/contrast rescue via `autoFixImage` on any page whose quality
  // scan flagged it as dark/bright/flat — BEFORE the file is uploaded. This
  // lifts OCR accuracy on the first try. Persisted in localStorage so a
  // power user's preference sticks across sessions.
  const [autoImprove, setAutoImprove] = useState(() => {
    try { return localStorage.getItem("rf_scan_autoimprove") !== "off"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("rf_scan_autoimprove", autoImprove ? "on" : "off"); }
    catch { /* ignore */ }
  }, [autoImprove]);
  const [mode, setMode] = useState(initialMode); // scan | manual | url | paste
  const [importUrl, setImportUrl] = useState(prefilledUrl);
  const [importing, setImporting] = useState(false);
  // Paste-text mode: raw text buffer + "parsing" spinner state. Same
  // sessionStorage → editor pipeline as URL import so the review UX is
  // identical no matter how the recipe arrived.
  const [pasteText, setPasteText] = useState("");
  const [parsing, setParsing] = useState(false);
  // Admin-editable copy for /scan (H1, intro, entry-way cards). Pulled from
  // /api/public/site-content/scan with the site_content_defaults.py copy as
  // the fall-back so the page still renders instantly if the fetch is slow.
  // Cached copy renders instantly on repeat visits; first visit waits for the
  // fetch (no hard-coded fallback flash before the admin copy swaps in).
  const [scanCopy, setScanCopy] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rf_scan_copy") || "null"); } catch { return null; }
  });
  const [copyReady, setCopyReady] = useState(() => !!scanCopy);
  useEffect(() => {
    api.get("/public/site-content/scan")
      .then((r) => {
        const c = r.data?.content || null;
        setScanCopy(c);
        if (c) localStorage.setItem("rf_scan_copy", JSON.stringify(c));
      })
      .catch(() => {})
      .finally(() => setCopyReady(true));
  }, []);

  const cameraInputRef = useRef(null);
  const modePanelRef = useRef(null);
  const [scrollTick, setScrollTick] = useState(0);
  const pickMode = (m) => { setMode(m); setScrollTick((t) => t + 1); };
  useEffect(() => {
    if (!scrollTick) return;
    const id = setTimeout(() => modePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    return () => clearTimeout(id);
  }, [scrollTick]);
  const fileInputRef = useRef(null);

  const addPages = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList).slice(0, 12 - pages.length).map((f) => ({
      file: f,
      previewUrl: URL.createObjectURL(f),
      quality: null, // filled in asynchronously by analysePhoto
    }));
    setPages((p) => [...p, ...incoming]);
    setMode("scan");
    // Fire-and-forget quality analysis per page. Non-blocking — the user
    // can hit "Start scan" straight away and the analysis will just be
    // ignored if it hasn't finished yet. Runs in ~150-400ms each.
    incoming.forEach((page) => {
      analysePhoto(page.file).then((quality) => {
        setPages((current) => current.map((p) =>
          p.previewUrl === page.previewUrl ? { ...p, quality } : p
        ));
      }).catch(() => { /* silent — quality is optional */ });
    });
  };

  const removePage = (idx) => {
    setPages((p) => {
      const next = [...p];
      const removed = next.splice(idx, 1)[0];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  // Auto-fix a single page (brightness + contrast rescue via histogram
  // stretch + gamma). Non-destructive: the original is dropped only after
  // the new File is produced, and we re-run analysePhoto so the badge
  // updates to reflect the improvement.
  const autoFixPage = async (idx) => {
    const target = pages[idx];
    if (!target || target.fixing) return;
    setPages((current) => current.map((p, i) => i === idx ? { ...p, fixing: true } : p));
    try {
      const result = await autoFixImage(target.file);
      if (!result) {
        toast.error("Couldn't auto-fix that photo", { description: "Try retaking it with more even light." });
        setPages((current) => current.map((p, i) => i === idx ? { ...p, fixing: false } : p));
        return;
      }
      const newPreview = URL.createObjectURL(result.file);
      setPages((current) => current.map((p, i) => {
        if (i !== idx) return p;
        URL.revokeObjectURL(p.previewUrl);
        return { file: result.file, previewUrl: newPreview, quality: null, fixing: false, wasAutoFixed: true };
      }));
      // Re-run quality analysis so the badge reflects the improvement.
      analysePhoto(result.file).then((quality) => {
        setPages((current) => current.map((p) =>
          p.previewUrl === newPreview ? { ...p, quality } : p
        ));
      }).catch(() => { /* silent */ });
      buzz([12, 30, 12]);
      toast.success("Photo brightened", { description: "Contrast boosted for the AI. Retake if the badge still shows an issue." });
    } catch {
      toast.error("Auto-fix failed", { description: "Please retake the photo in better light." });
      setPages((current) => current.map((p, i) => i === idx ? { ...p, fixing: false } : p));
    }
  };

  // Batch auto-fix: run auto-fix on every page that currently has a
  // brightness/contrast-related quality issue. Skips already-fixed pages
  // and pages whose only issue is blur/low-res (which auto-fix can't help).
  const autoFixAllProblemPages = async () => {
    const fixable = pages
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => p.quality && (
        p.quality.issues.includes("dark") ||
        p.quality.issues.includes("bright") ||
        p.quality.issues.includes("flat")
      ) && !p.fixing);
    if (fixable.length === 0) return;
    // Sequential so we don't spike memory on low-end phones fixing 12 pages.
    for (const { i } of fixable) {
      await autoFixPage(i);
    }
  };

  // Mobile-friendly feedback helpers. Vibration is supported on Android and
  // (with sound-focused hooks) on iOS 17+; the API is a no-op elsewhere so
  // it's safe to call unconditionally.
  const buzz = (pattern) => { try { navigator.vibrate?.(pattern); } catch { /* ignore */ } };

  // Turn a raw backend error into a message a phone user can act on. iOS
  // and Android both squash long toasts, so we keep the primary line short
  // and stash the actionable tip on a second line.
  const friendlyScanError = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || "";
    if (status === 402) return null; // caller opens paywall modal instead
    if (!err?.response) {
      return {
        title: "Scan didn't reach us",
        hint: "Check your connection and try again in a moment.",
      };
    }
    if (status === 413) {
      return {
        title: "That photo is too big for us to process",
        hint: "Reduce photo quality in your camera settings, or crop it before uploading.",
      };
    }
    if (status === 408 || detail.toLowerCase().includes("timeout")) {
      return {
        title: "Scan timed out",
        hint: "Try again — one page at a time works best on slow networks.",
      };
    }
    if (detail.toLowerCase().includes("no text") || detail.toLowerCase().includes("could not read") || detail.toLowerCase().includes("empty")) {
      return {
        title: "We couldn't read text from that image",
        hint: "Try a brighter photo, hold the phone flat above the page, and avoid glare.",
      };
    }
    if (status >= 500) {
      return {
        title: "Something went wrong on our end",
        hint: detail || "Please try again in a minute. If it keeps failing, email contactus@recipefiler.com with a screenshot so we can fix it.",
      };
    }
    return {
      title: "Scan couldn't be completed",
      hint: detail || "Try a clearer photo, switch to Manual mode, or email contactus@recipefiler.com if it keeps happening.",
    };
  };

  const startScan = async () => {
    if (pages.length === 0) { toast.error("Please add at least one page first"); return; }
    setLoading(true);
    try {
      // ─── AUTO-IMPROVE PASS ────────────────────────────────────────────
      // If the toggle is ON, silently brighten/contrast-fix any page whose
      // quality analysis flagged brightness/contrast issues. Skips pages
      // that either look fine, weren't analysed yet, were already fixed,
      // or whose only problem is blur/low-res (which auto-fix can't help).
      // We work on a local `filesToUpload` array so the flow doesn't rely
      // on React state having propagated by the time we hit FormData.
      const AUTO_FIX_ISSUES = new Set(["dark", "bright", "flat"]);
      const filesToUpload = [];
      let autoFixedCount = 0;
      for (const page of pages) {
        const shouldFix = autoImprove
          && page.quality?.analysed
          && !page.wasAutoFixed
          && (page.quality.issues || []).some((iss) => AUTO_FIX_ISSUES.has(iss));
        if (!shouldFix) {
          filesToUpload.push(page.file);
          continue;
        }
        try {
          const result = await autoFixImage(page.file);
          if (result?.file) {
            filesToUpload.push(result.file);
            autoFixedCount += 1;
          } else {
            filesToUpload.push(page.file);
          }
        } catch {
          filesToUpload.push(page.file); // fail-open — always ship the raw
        }
      }
      if (autoFixedCount > 0) {
        toast.success(`Improved ${autoFixedCount} photo${autoFixedCount === 1 ? "" : "s"}`, {
          description: "Brightness and contrast auto-fixed before scanning for a cleaner read.",
          duration: 2500,
        });
      }
      // ──────────────────────────────────────────────────────────────────

      // Track OCR-ok flag so we can distinguish a genuine "AI couldn't
      // read the image" outcome from a real scan success. The single-page
      // endpoint returns ocr_ok explicitly; the multi-page endpoint too.
      let ingredientCount = 0;
      let stepCount = 0;
      let recipeTitle = "";
      let ocrOk = true;
      if (filesToUpload.length === 1) {
        // Single-page: existing fast path.
        const form = new FormData();
        form.append("file", filesToUpload[0]);
        const { data } = await api.post("/recipes/scan", form, { headers: { "Content-Type": "multipart/form-data" } });
        track("scan_recipe", { pages: 1, scan_type: "single" });
        sessionStorage.setItem("scan_result", JSON.stringify({
          ...data,
          cookbook_id: presetCb,
          return_to: presetReturnTo,
          preview_url: pages[0].previewUrl,
          scan_pages: data.original_scan_url ? [data.original_scan_url] : [],
        }));
        ingredientCount = (data?.structured?.ingredients || []).length;
        stepCount = (data?.structured?.instructions || []).length;
        recipeTitle = data?.structured?.title || "";
        ocrOk = data?.ocr_ok !== false;
      } else {
        // Multi-page: server-side merge into one recipe.
        const form = new FormData();
        filesToUpload.forEach((f) => form.append("files", f));
        const { data } = await api.post("/recipes/scan-multi", form, { headers: { "Content-Type": "multipart/form-data" } });
        track("scan_recipe", { pages: filesToUpload.length, scan_type: "multi" });
        sessionStorage.setItem("scan_result", JSON.stringify({
          raw_text: data.raw_text,
          structured: data.structured,
          cookbook_id: presetCb,
          return_to: presetReturnTo,
          preview_url: pages[0].previewUrl,
          scan_pages: data.scan_pages || [],
          original_scan_url: data.original_scan_url,
        }));
        ingredientCount = (data?.structured?.ingredients || []).length;
        stepCount = (data?.structured?.instructions || []).length;
        recipeTitle = data?.structured?.title || "";
        ocrOk = data?.ocr_ok !== false;
      }
      // ─── FEEDBACK ─────────────────────────────────────────────────────
      // Distinguish three outcomes:
      //   1. OCR failed entirely (ocr_ok=false) → hard ERROR toast, do NOT
      //      navigate to sandbox (nothing to review).
      //   2. OCR worked but 0 ingredients / 0 steps parsed → soft warning,
      //      still navigate so the user can fill it in manually.
      //   3. All good → success toast with counts.
      if (!ocrOk) {
        buzz([100, 60, 100]);
        toast.error("We couldn't read text from that image", {
          description: "Our OCR came back empty. Try again with a clearer photo — good even light and no glare — or switch to Manual mode.",
          duration: 8000,
        });
        return; // stay on scan page; don't go to sandbox
      }
      buzz([15, 40, 15]); // di-di-dit success pattern
      if (ingredientCount === 0 && stepCount === 0) {
        toast.warning?.("Scan landed but we couldn't structure it", {
          description: "The text came through but we couldn't split ingredients and steps. Fill them in on the review screen.",
          duration: 6000,
        }) || toast("Scan landed but we couldn't structure it", {
          description: "The text came through but we couldn't split ingredients and steps. Fill them in on the review screen.",
          duration: 6000,
        });
      } else {
        toast.success("Scan successful", {
          description: `${ingredientCount} ingredient${ingredientCount === 1 ? "" : "s"} and ${stepCount} step${stepCount === 1 ? "" : "s"} found${recipeTitle ? ` in "${recipeTitle}"` : ""}. Review before saving.`,
          duration: 4500,
        });
      }
      // Brief delay so the phone toast is actually visible before navigating.
      setTimeout(() => nav("/sandbox"), 350);
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall({ open: true, message: e?.response?.data?.detail || "" });
        return;
      }
      // ─── FAILURE FEEDBACK ─────────────────────────────────────────────
      // Long buzz + user-friendly, mobile-aware message with a tip.
      buzz([100, 60, 100]); // long-short-long error pattern
      const friendly = friendlyScanError(e);
      if (friendly) {
        toast.error(friendly.title, { description: friendly.hint, duration: 7000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const startManual = () => {
    const empty = {
      raw_text: "",
      preview_url: null,
      cookbook_id: presetCb,
      return_to: presetReturnTo,
      structured: {
        title: "Untitled Recipe", creator_name: user?.username || user?.name || "", serves: 4,
        prep_minutes: 15, cook_minutes: 30, difficulty: "Medium",
        tools: [], ingredients: [{ name: "", quantity: 0, unit: "", notes: "" }], instructions: [""],
      },
    };
    sessionStorage.setItem("scan_result", JSON.stringify(empty));
    nav("/sandbox");
  };

  // Click handlers: each card actually FIRES the relevant action so the user sees something happen.
  // Guest guard: if the visitor hasn't signed in, redirect to /register with
  // a `next=` parameter pointing back at the specific mode they wanted. A
  // one-line toast explains what's happening so the flow doesn't feel like
  // a hijack. Without this guard, guests would hit the camera / file /
  // manual UI and only discover the 401 when they tried to save — a
  // frustrating dead-end.
  const guestSignupPunt = (mode) => {
    const next = encodeURIComponent(`/scan${mode && mode !== "scan" ? `?mode=${mode}` : ""}`);
    toast.info("Sign up first — it takes 10 seconds.", {
      description: "We'll bring you right back to add your recipe.",
      duration: 5000,
    });
    nav(`/register?next=${next}`);
  };
  const handleCameraCard = () => {
    if (!user) return guestSignupPunt("scan");
    setMode("scan");
    cameraInputRef.current?.click();
  };
  const handleUploadCard = () => {
    if (!user) return guestSignupPunt("scan");
    setMode("scan");
    fileInputRef.current?.click();
  };
  const handleManualCard = () => {
    if (!user) return guestSignupPunt("manual");
    pickMode("manual");
  };

  const handlePasteCard = () => {
    if (!user) return guestSignupPunt("paste");
    pickMode("paste");
  };

  const handleUrlCard = () => {
    if (!user) return guestSignupPunt("url");
    pickMode("url");
  };

  const handleSocialShareCard = () => {
    if (!user) return guestSignupPunt("scan");
    nav("/ios-share-setup");
  };

  // Registry that turns an admin-typed action_key into the correct icon +
  // click handler + selected-state ring. Keeps ScanRecipe.jsx as the single
  // source of truth for what each card DOES — the CMS only owns the copy.
  const CARD_ACTIONS = {
    camera: {
      icon: Camera,
      onClick: handleCameraCard,
      selected: mode === "scan" && pages.length === 0,
      testid: "scan-card-camera",
    },
    upload: {
      icon: UploadSimple,
      onClick: handleUploadCard,
      selected: false,
      testid: "scan-card-upload",
    },
    url: {
      icon: LinkSimple,
      onClick: handleUrlCard,
      selected: mode === "url",
      testid: "scan-card-url",
    },
    manual: {
      icon: PencilSimple,
      onClick: handleManualCard,
      selected: mode === "manual",
      testid: "scan-card-manual",
    },
    paste: {
      icon: ClipboardText,
      onClick: handlePasteCard,
      selected: mode === "paste",
      testid: "scan-card-paste",
    },
    social_share: {
      icon: ShareNetwork,
      onClick: handleSocialShareCard,
      selected: false,
      testid: "scan-card-social-share",
    },
  };

  const startUrlImport = async () => {
    const u = importUrl.trim();
    if (!/^https?:\/\//i.test(u)) { toast.error("Please paste a valid http(s) link"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/recipes/import-url", { url: u });
      sessionStorage.setItem("scan_result", JSON.stringify({ ...data, cookbook_id: presetCb, return_to: presetReturnTo, preview_url: null }));
      buzz([15, 40, 15]);
      const ingredientCount = (data?.structured?.ingredients || []).length;
      const stepCount = (data?.structured?.instructions || []).length;
      const src = data.source_name || data.source_type || "the link";
      toast.success("Import successful", {
        description: `Pulled from ${src} — ${ingredientCount} ingredient${ingredientCount === 1 ? "" : "s"} and ${stepCount} step${stepCount === 1 ? "" : "s"} found. Review before saving.`,
        duration: 4500,
      });
      setTimeout(() => nav("/sandbox"), 350);
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall({ open: true, message: e?.response?.data?.detail || "" });
      } else {
        buzz([100, 60, 100]);
        // URL imports fail in a few predictable ways — surface them clearly.
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || "";
        let title = "Import couldn't be completed";
        let hint = detail || "Try pasting the direct recipe URL rather than a share link.";
        if (!e?.response) {
          title = "Import didn't reach us";
          hint = "Check your connection and try again.";
        } else if (status === 400 || detail.toLowerCase().includes("could not")) {
          title = "That page doesn't look like a recipe";
          hint = "Some sites block automated readers. Try the recipe's direct URL, not a Google or social-share link.";
        } else if (status >= 500) {
          title = "Something went wrong on our end";
          hint = "Please try again in a moment.";
        }
        toast.error(title, { description: hint, duration: 7000 });
      }
    } finally {
      setImporting(false);
    }
  };

  const startPasteParse = async () => {
    const t = pasteText.trim();
    if (!t) { toast.error("Paste some recipe text first, then hit Parse."); return; }
    if (t.length > 5000) {
      toast.error("Please keep it under 5,000 characters.");
      return;
    }
    setParsing(true);
    try {
      const { data } = await api.post("/recipes/parse-text", { text: t });
      sessionStorage.setItem("scan_result", JSON.stringify({
        ...data, cookbook_id: presetCb, return_to: presetReturnTo, preview_url: null,
      }));
      buzz([15, 40, 15]);
      const ingredientCount = (data?.structured?.ingredients || []).length;
      const stepCount = (data?.structured?.instructions || []).length;
      toast.success("Text parsed", {
        description: `Found ${ingredientCount} ingredient${ingredientCount === 1 ? "" : "s"} and ${stepCount} step${stepCount === 1 ? "" : "s"}. Review before saving.`,
        duration: 4500,
      });
      setTimeout(() => nav("/sandbox"), 350);
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall({ open: true, message: e?.response?.data?.detail || "" });
      } else {
        buzz([100, 60, 100]);
        const detail = e?.response?.data?.detail || "The AI couldn't structure that text. Try tidying the paste or use the manual editor.";
        toast.error("Parse couldn't be completed", { description: detail, duration: 7000 });
      }
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
      <PageMeta title="Add a recipe" noindex />
      <Link to="/dashboard" className="text-sm font-bold text-rf-orange hover:underline mb-6 inline-block">← Back to My Cookbooks</Link>
      <div data-testid="scan-copy" className={`transition-opacity duration-200 ${copyReady ? "opacity-100" : "opacity-0"}`}>
        <p className="overline mb-3">{scanCopy?.overline || "Add a recipe"}</p>
        <h1 className="font-display text-4xl md:text-6xl font-black text-rf-ink mb-3 leading-[0.95]">
          {scanCopy?.h1_line1 || "Four ways"}<br />{scanCopy?.h1_line2 || "to add a recipe."}
        </h1>
        <p className="text-rf-ink-soft mb-10 max-w-xl">{scanCopy?.intro || "Snap, upload, paste a link, or type. Each lands in the same review Sandbox so you can polish before saving."}</p>
      </div>

      {/* Guest gate: guests used to be able to click the four cards, hit the
          camera/URL/manual flow, and then bump into a 401 when they tried to
          save. Show a friendly sign-up prompt instead so the intent is clear
          before they do any work. Preserves the current URL (with any mode/
          cookbook_id query params) via `next=` so post-signup the cook lands
          right back here. */}
      {!user ? (
        <div
          data-testid="scan-guest-gate"
          className="card-recipe mb-10 bg-gradient-to-br from-rf-amber/40 to-rf-orange/10 border-2 border-rf-amber"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <Sparkle size={48} weight="duotone" className="text-rf-orange flex-shrink-0" />
            <div className="flex-1">
              <h2 className="font-display font-black text-2xl text-rf-ink mb-2">Create a free account to save your first recipe.</h2>
              <p className="text-rf-ink-soft text-sm mb-4">
                It takes about 10 seconds. You&apos;ll get <b>10 free saved recipes</b>, a private Inbox for shared links, and a personal QR to swap recipes with family.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  data-testid="scan-guest-signup"
                  to={`/register?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="btn-ink"
                >
                  Create a free account →
                </Link>
                <Link
                  data-testid="scan-guest-signin"
                  to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="btn-outline-ink"
                >
                  I already have one — Sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10" data-testid="scan-entry-cards">
        {(scanCopy?.cards || []).map((card, idx) => {
          const action = CARD_ACTIONS[card.action_key];
          // Skip cards with an unknown action_key so a typo in the CMS
          // can't render a card that does nothing when tapped.
          if (!action) return null;
          const Icon = action.icon;
          return (
            <button
              key={`${card.action_key}-${idx}`}
              data-testid={action.testid}
              type="button"
              aria-pressed={action.selected}
              onClick={action.onClick}
              className={`card-recipe max-sm:!p-4 text-left hover:ring-2 hover:ring-rf-amber transition-all ${action.selected ? "ring-2 ring-rf-amber" : ""}`}
            >
              <Icon size={32} weight="duotone" className="text-rf-orange mb-2 sm:mb-3 max-sm:w-7 max-sm:h-7" />
              <h3 className="font-display font-extrabold text-base sm:text-lg text-rf-ink leading-tight">{card.title}</h3>
              <p className="hidden sm:block text-sm text-rf-ink-soft">{card.body}</p>
              <p className="text-xs text-rf-orange font-bold mt-1 sm:mt-2">{card.cta}</p>
            </button>
          );
        })}
      </div>

      {/* Hidden inputs driven by the cards above */}
      <input
        ref={cameraInputRef}
        data-testid="scan-camera-input"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => addPages(e.target.files)}
      />
      <input
        ref={fileInputRef}
        data-testid="scan-file-input"
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx"
        className="hidden"
        onChange={(e) => addPages(e.target.files)}
      />

      <div ref={modePanelRef} data-testid="scan-mode-panel" className="scroll-mt-24">
      {mode === "scan" ? (
        <div className="card-recipe">
          <div
            data-testid="scan-dropzone"
            onClick={() => pages.length === 0 && fileInputRef.current?.click()}
            className={`block border-2 border-dashed border-rf-ink/20 rounded-2xl p-8 text-center ${pages.length === 0 ? "cursor-pointer hover:bg-rf-cream-alt" : ""} transition-colors`}
          >
            {pages.length === 0 ? (
              <>
                <Camera size={64} weight="duotone" className="mx-auto text-rf-orange mb-4" />
                <p className="font-display font-bold text-rf-ink text-lg mb-1">Upload a recipe or click to choose</p>
                <p className="text-sm text-rf-ink-soft mb-1">JPG, PNG, WEBP, PDF, DOC, DOCX — up to ~25 MB each</p>
                <p className="text-xs text-rf-orange font-bold">📖 Multi-page recipe? Add up to 12 pages — we&apos;ll merge them into one.</p>
              </>
            ) : (
              <div data-testid="scan-pages-gallery">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-4">
                  {pages.map((p, idx) => {
                    const canFix = p.quality && (
                      p.quality.issues.includes("dark") ||
                      p.quality.issues.includes("bright") ||
                      p.quality.issues.includes("flat")
                    );
                    return (
                    <div key={idx} className="relative group" data-testid={`scan-page-${idx}`}>
                      <img src={p.previewUrl} alt="" className={`w-full aspect-[3/4] object-cover rounded-lg border-2 ${p.wasAutoFixed ? "border-green-500/70" : "border-rf-ink/10"}`} />
                      <span className="absolute top-1 left-1 bg-rf-ink text-rf-cream text-[10px] font-black px-1.5 py-0.5 rounded-md">Page {idx + 1}</span>
                      {p.wasAutoFixed && (
                        <span
                          data-testid={`scan-page-autofixed-${idx}`}
                          className="absolute top-1 left-1/2 -translate-x-1/2 bg-green-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-md"
                          title="This photo was auto-brightened before scanning"
                        >
                          Auto-fixed
                        </span>
                      )}
                      {p.quality && p.quality.level !== "ok" && (
                        <span
                          data-testid={`scan-page-quality-${idx}`}
                          title={p.quality.tips[0] || ""}
                          className={`absolute bottom-1 left-1 text-[10px] font-black px-1.5 py-0.5 rounded-md ${p.quality.level === "poor" ? "bg-red-600 text-white" : "bg-rf-amber text-rf-ink"}`}
                        >
                          {p.quality.level === "poor" ? "⚠ " : "! "}
                          {p.quality.issues.includes("blurry") ? "Blurry" :
                           p.quality.issues.includes("dark") ? "Dark" :
                           p.quality.issues.includes("bright") ? "Bright" :
                           p.quality.issues.includes("low_res") ? "Low-res" :
                           p.quality.issues.includes("flat") ? "Low contrast" : "Check"}
                        </span>
                      )}
                      {canFix && (
                        <button
                          type="button"
                          data-testid={`scan-page-autofix-${idx}`}
                          disabled={p.fixing}
                          onClick={(e) => { e.stopPropagation(); autoFixPage(idx); }}
                          className="absolute bottom-1 right-1 bg-rf-orange text-white text-[10px] font-black px-1.5 py-0.5 rounded-md hover:bg-rf-orange/90 disabled:opacity-60 shadow-md"
                          title="Auto-brighten and boost contrast for this page"
                        >
                          {p.fixing ? "…" : "✨ Auto-fix"}
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`scan-page-remove-${idx}`}
                        onClick={(e) => { e.stopPropagation(); removePage(idx); }}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-600 text-white text-xs font-black opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove this page"
                      >✕</button>
                    </div>
                    );
                  })}
                  {pages.length < 12 && (
                    <button
                      type="button"
                      data-testid="scan-add-page-btn"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-[3/4] rounded-lg border-2 border-dashed border-rf-orange/50 bg-rf-amber/15 hover:bg-rf-amber/30 text-rf-orange font-bold text-sm flex flex-col items-center justify-center transition-colors"
                    >
                      <span className="text-2xl leading-none">+</span>
                      <span className="text-[11px] mt-1 leading-tight">Add another page</span>
                    </button>
                  )}
                </div>

                {/* Quality summary — appears when any page has issues. Tells
                    the cook exactly what's wrong and how to fix it BEFORE
                    they burn 30s on an AI scan that would fail. */}
                {(() => {
                  const poor = pages.filter((p) => p.quality?.level === "poor");
                  const warn = pages.filter((p) => p.quality?.level === "warn");
                  if (poor.length === 0 && warn.length === 0) return null;
                  const allTips = Array.from(new Set(
                    [...poor, ...warn].flatMap((p) => p.quality?.tips || [])
                  )).slice(0, 3);
                  // Only offer auto-fix when the issue is brightness/contrast
                  // (dark, bright, flat). Blur & low-res can't be rescued in
                  // canvas — the cook must retake the photo.
                  const fixableCount = pages.filter((p) => p.quality && (
                    p.quality.issues.includes("dark") ||
                    p.quality.issues.includes("bright") ||
                    p.quality.issues.includes("flat")
                  )).length;
                  const anyFixing = pages.some((p) => p.fixing);
                  return (
                    <div
                      data-testid="scan-quality-banner"
                      className={`rounded-2xl p-4 mb-4 border-2 ${poor.length > 0 ? "border-red-500/60 bg-red-50" : "border-rf-amber bg-rf-amber/20"}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1">
                          <p className="font-display font-black text-sm text-rf-ink mb-1">
                            {poor.length > 0
                              ? `⚠ ${poor.length === 1 ? "One photo looks tricky" : `${poor.length} photos look tricky`} for the AI to read`
                              : `! ${warn.length === 1 ? "One photo could be sharper" : `${warn.length} photos could be sharper`}`}
                          </p>
                          <ul className="text-xs text-rf-ink-soft space-y-1 mt-2">
                            {allTips.map((tip, i) => <li key={i}>· {tip}</li>)}
                          </ul>
                          <p className="text-[11px] text-rf-ink-soft mt-2 italic">
                            You can still Run AI scan — this is just a heads-up. Tap ✕ on a page to remove it and reshoot.
                          </p>
                        </div>
                        {fixableCount > 0 && (
                          <button
                            type="button"
                            data-testid="scan-quality-autofix-all"
                            disabled={anyFixing}
                            onClick={autoFixAllProblemPages}
                            className="btn-amber whitespace-nowrap self-start disabled:opacity-60"
                            title="Brighten and boost contrast on affected pages"
                          >
                            {anyFixing
                              ? <><span className="animate-spin">⌖</span> Fixing…</>
                              : <><Sparkle size={16} /> Auto-fix {fixableCount > 1 ? `${fixableCount} photos` : "photo"}</>}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <p className="text-xs text-rf-ink-soft">
                  {pages.length === 1 ? "1 page selected — single-recipe scan." : `${pages.length} pages — will be merged into ONE recipe. Pages stay attached to the recipe.`}
                </p>
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-between gap-3 flex-wrap items-center">
            {pages.length > 0 && (
              <button
                type="button"
                onClick={() => { pages.forEach((p) => URL.revokeObjectURL(p.previewUrl)); setPages([]); }}
                className="text-sm font-bold text-rf-ink-soft hover:text-rf-orange"
              >
                Start over
              </button>
            )}
            <label
              data-testid="scan-autoimprove-toggle"
              className="inline-flex items-center gap-2 text-xs font-bold text-rf-ink-soft cursor-pointer select-none ml-auto"
              title="Silently brightens and boosts contrast on dim photos before OCR runs. On by default."
            >
              <input
                type="checkbox"
                checked={autoImprove}
                onChange={(e) => setAutoImprove(e.target.checked)}
                data-testid="scan-autoimprove-checkbox"
                className="h-4 w-4 rounded border-rf-ink/30 text-rf-orange focus:ring-rf-orange"
              />
              <span className="inline-flex items-center gap-1">
                <Sparkle size={14} weight="duotone" className="text-rf-orange" />
                Auto-improve photos
              </span>
            </label>
            <button data-testid="scan-start-btn" disabled={pages.length === 0 || loading} onClick={startScan} className="btn-amber">
              {loading ? (
                <><span className="animate-spin">⌖</span> {pages.length > 1 ? `Scanning ${pages.length} pages…` : "Scanning…"}</>
              ) : (
                <><Sparkle size={18} /> {pages.length > 1 ? `Run AI scan (${pages.length} pages)` : "Run AI scan"}</>
              )}
            </button>
          </div>
        </div>
      ) : mode === "url" ? (
        <div className="card-recipe">
          <p className="text-rf-ink-soft mb-4">
            Paste a link from any cookery site, an Instagram or TikTok post, Pinterest,
            or a YouTube video. We&apos;ll grab the recipe and tag where it came from
            so you can credit the source.
          </p>
          <div className="flex gap-2 flex-wrap text-rf-ink-soft text-xs mb-3">
            <span className="inline-flex items-center gap-1 chip"><LinkSimple size={12} weight="bold" /> Cookery sites</span>
            <span className="inline-flex items-center gap-1 chip"><InstagramLogo size={12} weight="bold" /> Instagram</span>
            <span className="inline-flex items-center gap-1 chip"><TiktokLogo size={12} weight="bold" /> TikTok</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="import-url-input"
              type="url"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://yourfavouritecookerysite.com/recipes/..."
              className="input-rf flex-1"
              onKeyDown={(e) => { if (e.key === "Enter") startUrlImport(); }}
            />
            <button
              data-testid="import-url-btn"
              disabled={importing || !importUrl.trim()}
              onClick={startUrlImport}
              className="btn-amber"
            >
              {importing ? <><span className="animate-spin">⌖</span> Importing…</> : <><Sparkle size={18} /> Import recipe</>}
            </button>
          </div>
          <p className="text-xs text-rf-ink-soft mt-3 italic">
            Tip: if Instagram or TikTok blocks our fetch, copy the post&apos;s caption and use the manual editor instead.
          </p>
        </div>
      ) : mode === "paste" ? (
        <div className="card-recipe" data-testid="paste-mode-panel">
          <p className="text-rf-ink-soft mb-4">
            Copy any recipe text — an email from Mum, a WhatsApp message, an
            Apple Note, or a paragraph from a blog — and drop it below.
            AI structures it into ingredients + steps so you land straight in the
            review editor.
          </p>
          <textarea
            data-testid="paste-text-input"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value.slice(0, 5000))}
            placeholder="Paste your recipe here…"
            rows={12}
            className="input-rf w-full font-mono text-sm leading-relaxed"
          />
          <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
            <p className="text-xs text-rf-ink-soft" data-testid="paste-text-counter">
              {pasteText.length.toLocaleString()} / 5,000 characters
            </p>
            <button
              data-testid="paste-parse-btn"
              disabled={parsing || !pasteText.trim()}
              onClick={startPasteParse}
              className="btn-amber"
            >
              {parsing ? <><span className="animate-spin">⌖</span> Parsing…</> : <><Sparkle size={18} /> Parse with AI</>}
            </button>
          </div>
          <p className="text-xs text-rf-ink-soft mt-3 italic">
            Tip: the more structured the paste, the better the parse. Ingredient list on separate lines beats one long paragraph.
          </p>
        </div>
      ) : (
        <div className="card-recipe">
          <p className="text-rf-ink-soft mb-6">Type a recipe by hand. You&apos;ll get the full editor on the next screen.</p>
          <button onClick={startManual} data-testid="manual-start-btn" className="btn-amber"><PencilSimple size={18} /> Open editor</button>
        </div>
      )}
      </div>

      <PaywallModal
        open={paywall.open}
        onClose={() => setPaywall({ open: false, message: "" })}
        message={paywall.message}
        upgradesEnabled={flags.subscriptions_enabled}
        recipeCount={user?.recipe_count}
        freeLimit={limits?.free_recipe_limit}
      />
    </div>
  );
}
